import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, ArrowDown, ArrowUp, ArrowUpDown, CalendarDays, Clock, Coins, Info, LayoutGrid, Package, RefreshCw, ShoppingBag,
  Trophy, Undo2, Users,
} from 'lucide-react';
import { apiService, type ShopStatsResponse } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { formatPlayerName } from '@/utils/playerName';
import { ColumnChart, DonutChart, type ColumnDatum } from './JobsCharts';
import { dayLabel, formatInstant, shortDay } from './jobStats';
import { medal, shortUuid } from './moneyLeaderboard';
import {
  SHOP_CONFIG_FILE, SHOP_CONFIG_KEYS, SHOP_SATELLITE_VERSION, categoryColor, categoryLabel, categoryOf, categoryViews, currencyUnit,
  dailyByCategory, describe, formatPoints, nextSort, orderCategories, parseCatalog, productViews, sortRows,
  type ShopCatalog, type ShopCategoryView, type ShopProductView, type SortState,
} from './shopStats';
import lb from './MoneyLeaderboardSection.module.scss';
import styles from './JobsStatsSection.module.scss';
import sh from './ShopSection.module.scss';

/** Même choix de période que la section Box. */
const PERIODS = [7, 14, 30, 90] as const;

/** Lignes du tableau des produits avant « Voir tous les produits ». */
const PRODUCT_ROWS = 25;

/** Derniers achats montrés avant « Voir les 100 derniers achats » (le satellite en rend 100). */
const RECENT_ROWS = 20;

type ProductSortKey = 'name' | 'category' | 'purchases' | 'buyers' | 'price' | 'net' | 'seasonal' | 'classic' | 'refunds' | 'keys';
type CategorySortKey = 'label' | 'purchases' | 'buyers' | 'products' | 'net' | 'refunded' | 'share';

type CatalogState = { catalog: ShopCatalog | null; source: string | null; why: string | null };

/**
 * Catalogue de la boutique du projet, lu dans la config de RecubeBoutiqueMod de ses images (cosmetic.config.json :
 * noms, sections, prix). Sans lui (grade sans accès aux configs, projet sans boutique), la section montre les
 * identifiants et devine les catégories.
 */
function useShopCatalog(gameId: string): CatalogState {
  const [state, setState] = useState<CatalogState>({ catalog: null, source: null, why: null });
  useEffect(() => {
    let alive = true;
    setState({ catalog: null, source: null, why: null });
    void apiService.inspectContainerConfig({ fileNames: [SHOP_CONFIG_FILE], keys: [...SHOP_CONFIG_KEYS] }).then((res) => {
      if (!alive) return;
      const catalog = res.data?.success ? parseCatalog(res.data.sections) : null;
      if (catalog) {
        setState({ catalog, source: res.data?.referenceContainerName ?? null, why: null });
        return;
      }
      const why = res.error === 'Permission denied'
        ? "ton grade n'a pas accès aux configs des images"
        : res.data?.message?.trim() || res.error?.trim() || 'config illisible';
      setState({ catalog: null, source: null, why });
    });
    return () => { alive = false; };
  }, [gameId]);
  return state;
}

/**
 * Section « Boutique » de l'espace Statistiques : ce que les joueurs achètent dans la boutique en jeu (cosmétiques,
 * packs, clés et box, grades, lucky blocks...), combien d'acheteurs, les points boutique dépensés et remboursés, par
 * catégorie, par produit et par jour, les plus gros acheteurs et les derniers achats. Lu par le satellite 1.0.33 dans
 * l'historique des points boutique ; noms et catégories pris dans la config de la boutique de l'image.
 */
export function ShopSection() {
  const gameId = useGameStore((s) => s.activeGameId);
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<ShopStatsResponse | null>(null);
  const [error, setError] = useState<{ status?: number; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<string | null>(null);
  const [productSort, setProductSort] = useState<SortState<ProductSortKey>>({ key: 'net', dir: 'desc' });
  const [categorySort, setCategorySort] = useState<SortState<CategorySortKey>>({ key: 'net', dir: 'desc' });
  const [allProducts, setAllProducts] = useState(false);
  const [allRecent, setAllRecent] = useState(false);
  const { catalog, source, why } = useShopCatalog(gameId);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiService.getShopStats(days);
    if (res.data) { setData(res.data); setError(null); }
    else {
      setData(null);
      setError({
        status: res.status,
        message: res.status === 404
          ? `Le satellite de ce projet ne sert pas encore les statistiques de la boutique : il faut le mettre à jour (satellite ${SHOP_SATELLITE_VERSION} ou plus, à redéployer dans Déploiement > APIs).`
          : res.status === 403 ? "Ton grade n'a pas accès à cette section des statistiques."
            : res.error?.trim() || "Les achats de la boutique n'ont pas pu être lus.",
      });
    }
    setLoading(false);
  }, [days, gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void load(); }, [load]);
  // Un autre projet n'a pas les mêmes catégories.
  useEffect(() => { setCategory(null); }, [gameId]);

  const products = useMemo(() => productViews(data?.products ?? [], catalog), [data, catalog]);
  const categories = useMemo(() => categoryViews(products, catalog), [products, catalog]);
  const daily = useMemo(() => dailyByCategory(data?.daily ?? [], catalog), [data, catalog]);
  const present = useMemo(
    () => orderCategories([...categories.map((c) => c.id), ...daily.flatMap((d) => Object.keys(d.points))]),
    [categories, daily],
  );

  if (loading && !data && !error) return <div className={lb.state}><div className={lb.spinner} /><p>Lecture des achats de la boutique...</p></div>;
  if (error && !data) {
    return (
      <div className={lb.state}>
        {error.status === 404 ? <Info size={32} className={styles.stateIcon} /> : <AlertCircle size={32} />}
        <h3>{error.status === 404 ? 'Satellite à mettre à jour' : 'Boutique illisible'}</h3>
        <p className={styles.stateText}>{error.message}</p>
        <button className={lb.buttonGhost} onClick={() => void load()}><RefreshCw size={16} /> Vérifier de nouveau</button>
      </div>
    );
  }
  if (!data) return null;
  if (!data.available) return <div className={lb.state}><ShoppingBag size={32} /><h3>Pas d'achats de la boutique ici</h3><p>{data.reason}</p></div>;

  const t = data.totals!;
  const currency = catalog?.currency ?? null;
  const fmt = (n: number) => formatPoints(n, currency);
  const unit = currencyUnit(currency);
  const count = (n: number) => n.toLocaleString('fr-FR');
  const plural = (n: number, word: string) => `${count(n)} ${word}${Math.abs(n) > 1 ? 's' : ''}`;
  const spent = t.spentSeasonal + t.spentClassic;
  const refunded = t.refundSeasonal + t.refundClassic;
  const net = spent - refunded;
  const labelOf = (id: string) => categoryLabel(id, catalog);
  const colorOf = (id: string) => categoryColor(id, present);

  const cards = [
    { icon: ShoppingBag, value: count(t.purchases), label: 'Achats', sub: `${plural(t.products, 'produit')} vendu${t.products > 1 ? 's' : ''}` },
    {
      icon: Users, value: count(t.buyers), label: 'Acheteurs',
      sub: t.buyers > 0 ? `${fmt(net / t.buyers)} par acheteur en moyenne` : 'Joueurs distincts sur la période',
    },
    { icon: Coins, value: fmt(spent), label: 'Points dépensés', sub: `${fmt(t.spentSeasonal)} saisonniers, ${fmt(t.spentClassic)} classiques` },
    {
      icon: Undo2, value: fmt(refunded), label: 'Points remboursés',
      sub: t.refunds > 0 ? `${plural(t.refunds, 'achat')} rendu${t.refunds > 1 ? 's' : ''} (livraison ratée)` : 'Aucun achat remboursé',
    },
  ];

  const sortedCategories = sortRows(categories, (c) => categoryValue(c, categorySort.key), categorySort.dir);
  const shownProducts = category == null ? products : products.filter((p) => p.category === category);
  const sortedProducts = sortRows(shownProducts, (p) => productValue(p, productSort.key, catalog), productSort.dir);
  const productRows = allProducts ? sortedProducts : sortedProducts.slice(0, PRODUCT_ROWS);
  const withKeys = shownProducts.some((p) => p.keys != null);
  const recent = allRecent ? data.recent : data.recent.slice(0, RECENT_ROWS);

  const pointCols: ColumnDatum[] = daily.map((d) => ({
    key: d.date, label: shortDay(d.date), title: dayLabel(d.date),
    segments: present.map((c) => ({ id: c, label: labelOf(c), value: Math.max(0, d.points[c] ?? 0), color: colorOf(c) })),
  }));
  const purchaseCols: ColumnDatum[] = daily.map((d) => ({
    key: d.date, label: shortDay(d.date), title: dayLabel(d.date),
    segments: present.map((c) => ({ id: c, label: labelOf(c), value: d.purchases[c] ?? 0, color: colorOf(c) })),
    note: plural(d.buyers, 'acheteur'),
  }));
  const legend = present.map((c) => ({ id: c, label: labelOf(c), color: colorOf(c) }));

  return (
    <div className={styles.section}>
      <div className={lb.cards}>
        {cards.map((c) => (
          <div key={c.label} className={lb.card}>
            <div className={lb.cardIcon}><c.icon size={22} strokeWidth={2} /></div>
            <div className={lb.cardText}>
              <span className={lb.cardValue}>{c.value}</span>
              <span className={lb.cardLabel}>{c.label}</span>
              <span className={lb.cardSub}>{c.sub}</span>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.info}><Info size={16} />
        <span>
          Achats faits dans la boutique en jeu ces {data.days} derniers jours
          {data.since ? ` (première vente connue le ${formatInstant(data.since).split(' à ')[0]})` : ''}, notés dans
          l'historique des points boutique. Un achat payé en points saisonniers puis classiques compte une fois. Un achat
          remboursé (livraison ratée) ne compte ni dans les achats ni dans les acheteurs : ses points sont dans les points
          remboursés. Points nets : dépensés moins remboursés.
        </span>
      </div>
      <p className={styles.muted}>
        {source
          ? `Noms, catégories et prix lus dans la config de la boutique de l'image ${source} (${SHOP_CONFIG_FILE}).`
          : why
            ? `Config de la boutique non lue (${why}) : les produits gardent leur identifiant et leur catégorie est devinée d'après lui.`
            : 'Lecture de la config de la boutique...'}
      </p>

      <section className={styles.block}>
        <div className={styles.blockHead}>
          <h3><LayoutGrid size={18} /> Par catégorie</h3>
          <select className={lb.select} value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Période">
            {PERIODS.map((p) => <option key={p} value={p}>{p} jours</option>)}
          </select>
        </div>
        {categories.length === 0 ? <p className={lb.empty}>Aucun achat sur la période.</p> : (
          <div className={sh.stack}>
            <div className={sh.donutWrap}>
              <DonutChart ariaLabel="Points nets par catégorie" formatValue={fmt} centerValue={count(t.purchases)} centerLabel="achats"
                slices={categories.map((c) => ({
                  key: c.id, label: c.label, value: Math.max(0, c.net), color: c.color, sub: plural(c.purchases, 'achat'),
                }))} />
            </div>
            <div className={lb.tableWrap}>
              <table className={lb.table}>
                <thead>
                  <tr>
                    <SortTh id="label" label="Catégorie" sort={categorySort} onSort={setCategorySort} textual />
                    <SortTh id="purchases" label="Achats" sort={categorySort} onSort={setCategorySort} num />
                    <SortTh id="buyers" label="Acheteurs" sort={categorySort} onSort={setCategorySort} num />
                    <SortTh id="products" label="Produits" sort={categorySort} onSort={setCategorySort} num />
                    <SortTh id="net" label="Points nets" sort={categorySort} onSort={setCategorySort} num title="Dépensés moins remboursés" />
                    <SortTh id="refunded" label="Remboursés" sort={categorySort} onSort={setCategorySort} num title="Points remboursés (livraison ratée)" />
                    <SortTh id="share" label="Part" sort={categorySort} onSort={setCategorySort} num />
                  </tr>
                </thead>
                <tbody>
                  {sortedCategories.map((c) => (
                    <tr key={c.id} className={`${sh.clickable} ${category === c.id ? sh.selected : ''}`}
                      onClick={() => setCategory(category === c.id ? null : c.id)} title="Voir ses produits">
                      <td><span className={sh.categoryCell}><span className={styles.swatch} style={{ background: c.color }} />{c.label}</span></td>
                      <td className={lb.num}>{count(c.purchases)}</td>
                      <td className={lb.num}>{count(c.buyers)}</td>
                      <td className={lb.num}>{count(c.products)}</td>
                      <td className={lb.num}>{fmt(c.net)}</td>
                      <td className={lb.num}>{c.refunded > 0 ? fmt(c.refunded) : '-'}</td>
                      <td className={lb.num}>{percent(c.share)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      <section className={styles.block}>
        <div className={styles.blockHead}><h3><CalendarDays size={18} /> Par jour</h3></div>
        <div className={styles.grid2}>
          <div>
            <h4 className={styles.blockSub}>Points nets ({unit})</h4>
            <ColumnChart data={pointCols} formatValue={fmt} formatTick={(n) => count(n)} ariaLabel="Points nets dépensés à la boutique par jour"
              height={200} legend={legend} />
          </div>
          <div>
            <h4 className={styles.blockSub}>Achats</h4>
            <ColumnChart data={purchaseCols} formatValue={count} ariaLabel="Achats à la boutique par jour" height={200} legend={legend}
              totalLabel="Achats" />
          </div>
        </div>
      </section>

      <section className={styles.block}>
        <div className={styles.blockHead}>
          <h3><Package size={18} /> Par produit</h3>
          <span className={lb.tableCount}>{plural(shownProducts.length, 'produit')}</span>
        </div>
        {categories.length > 1 && (
          <div className={styles.chips} role="group" aria-label="Catégorie affichée">
            <button className={`${styles.chip} ${category == null ? styles.chipActive : ''}`} onClick={() => setCategory(null)}>Toutes les catégories</button>
            {categories.map((c) => (
              <button key={c.id} className={`${styles.chip} ${category === c.id ? styles.chipActive : ''}`} onClick={() => setCategory(c.id)}>
                <span className={styles.swatch} style={{ background: c.color }} /> {c.label}
              </button>
            ))}
          </div>
        )}
        {shownProducts.length === 0 ? <p className={lb.empty}>Aucun produit vendu sur la période.</p> : (
          <>
            <div className={lb.tableWrap}>
              <table className={lb.table}>
                <thead>
                  <tr>
                    <SortTh id="name" label="Produit" sort={productSort} onSort={setProductSort} textual />
                    {category == null && <SortTh id="category" label="Catégorie" sort={productSort} onSort={setProductSort} textual />}
                    <SortTh id="purchases" label="Achats" sort={productSort} onSort={setProductSort} num />
                    <SortTh id="buyers" label="Acheteurs" sort={productSort} onSort={setProductSort} num />
                    {withKeys && <SortTh id="keys" label="Clés" sort={productSort} onSort={setProductSort} num title="Clés données par les achats" />}
                    <SortTh id="price" label="Prix" sort={productSort} onSort={setProductSort} num title="Prix de la config, sinon celui du dernier achat" />
                    <SortTh id="net" label="Points nets" sort={productSort} onSort={setProductSort} num title="Dépensés moins remboursés" />
                    <SortTh id="seasonal" label="Saisonniers" sort={productSort} onSort={setProductSort} num />
                    <SortTh id="classic" label="Classiques" sort={productSort} onSort={setProductSort} num />
                    <SortTh id="refunds" label="Rendus" sort={productSort} onSort={setProductSort} num title="Achats remboursés (livraison ratée)" />
                  </tr>
                </thead>
                <tbody>
                  {productRows.map((p) => (
                    <tr key={p.key}>
                      <td title={p.id}>
                        <span className={sh.product}>
                          <span className={sh.productName}>{p.name}</span>
                          {p.detail && <span className={sh.productSub}>{p.detail}</span>}
                        </span>
                      </td>
                      {category == null && (
                        <td><span className={sh.categoryCell}><span className={styles.swatch} style={{ background: colorOf(p.category) }} />{labelOf(p.category)}</span></td>
                      )}
                      <td className={lb.num}>{count(p.purchases)}</td>
                      <td className={lb.num}>{count(p.buyers)}</td>
                      {withKeys && <td className={lb.num}>{p.keys != null ? count(p.keys) : '-'}</td>}
                      <td className={lb.num}>{p.price != null ? fmt(p.price) : '?'}</td>
                      <td className={lb.num}>{fmt(p.net)}</td>
                      <td className={lb.num}>{fmt(p.spentSeasonal)}</td>
                      <td className={lb.num}>{fmt(p.spentClassic)}</td>
                      <td className={lb.num}>{p.refunds > 0 ? count(p.refunds) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {sortedProducts.length > PRODUCT_ROWS && (
              <button className={lb.buttonGhost} onClick={() => setAllProducts((v) => !v)}>
                {allProducts ? 'Voir moins de produits' : `Voir tous les produits (${count(sortedProducts.length)})`}
              </button>
            )}
            <p className={styles.muted}>
              Cliquer sur un en-tête trie le tableau. Un cosmétique acheté dans le magasin et dans la sélection du jour n'a
              qu'une ligne. Les clés gagnées autrement (vote, pass, grades, événements) ne passent pas par la boutique.
            </p>
          </>
        )}
      </section>

      <div className={styles.grid2}>
        <section className={styles.block}>
          <div className={styles.blockHead}><h3><Clock size={18} /> Derniers achats</h3></div>
          {data.recent.length === 0 ? <p className={lb.empty}>Aucun achat sur la période.</p> : (
            <>
              <div className={lb.tableWrap}>
                <table className={lb.table}>
                  <thead><tr><th>Quand</th><th>Joueur</th><th>Produit</th><th className={lb.num}>Prix</th></tr></thead>
                  <tbody>
                    {recent.map((r) => {
                      const d = describe(r.kind, r.product, catalog);
                      const c = categoryOf(r.kind, r.product, catalog);
                      return (
                        <tr key={`${r.at}-${r.uuid}-${r.kind}-${r.product}`}>
                          <td className={sh.when}>{formatInstant(r.at)}</td>
                          <td><span className={lb.playerName}>{formatPlayerName(r.name, r.displayName, shortUuid(r.uuid))}</span></td>
                          <td title={r.product}>
                            <span className={sh.product}>
                              <span className={sh.categoryCell}><span className={styles.swatch} style={{ background: colorOf(c) }} />{d.name}</span>
                              {r.refunded && <span className={sh.refunded}>Remboursé</span>}
                            </span>
                          </td>
                          <td className={lb.num} title={`${fmt(r.seasonal)} saisonniers, ${fmt(r.classic)} classiques`}>{fmt(r.seasonal + r.classic)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {data.recent.length > RECENT_ROWS && (
                <button className={lb.buttonGhost} onClick={() => setAllRecent((v) => !v)}>
                  {allRecent ? 'Voir moins' : `Voir les ${count(data.recent.length)} derniers achats`}
                </button>
              )}
            </>
          )}
        </section>

        <section className={styles.block}>
          <div className={styles.blockHead}><h3><Trophy size={18} /> Plus gros acheteurs</h3></div>
          {data.topBuyers.length === 0 ? <p className={lb.empty}>Personne sur la période.</p> : (
            <div className={lb.tableWrap}>
              <table className={lb.table}>
                <thead><tr><th>Rang</th><th>Joueur</th><th className={lb.num}>Achats</th><th className={lb.num}>Points nets</th><th>Dernier achat</th></tr></thead>
                <tbody>
                  {data.topBuyers.map((b) => {
                    const m = medal(b.rank);
                    return (
                      <tr key={b.uuid}>
                        <td><span className={`${lb.rank} ${m ? lb[m] : ''}`}>{b.rank}</span></td>
                        <td><span className={lb.playerName}>{formatPlayerName(b.name, b.displayName, shortUuid(b.uuid))}</span></td>
                        <td className={lb.num}>{count(b.purchases)}</td>
                        <td className={lb.num}>{fmt(b.points)}</td>
                        <td className={sh.when}>{formatInstant(b.lastAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className={styles.muted}>Points nets dépensés sur la période, achats remboursés déduits.</p>
        </section>
      </div>

      {data.namesPartial && (
        <div className={lb.notice}><AlertCircle size={16} /><span>Certains joueurs n'ont pas encore de pseudo connu : leur uuid est affiché à la place.</span></div>
      )}
    </div>
  );
}

function percent(share: number): string {
  return `${(share * 100).toLocaleString('fr-FR', { maximumFractionDigits: share < 0.1 ? 1 : 0 })} %`;
}

function categoryValue(c: ShopCategoryView, key: CategorySortKey): number | string {
  switch (key) {
    case 'label': return c.label;
    case 'purchases': return c.purchases;
    case 'buyers': return c.buyers;
    case 'products': return c.products;
    case 'refunded': return c.refunded;
    case 'share': return c.share;
    default: return c.net;
  }
}

function productValue(p: ShopProductView, key: ProductSortKey, catalog: ShopCatalog | null): number | string | null {
  switch (key) {
    case 'name': return p.name;
    case 'category': return categoryLabel(p.category, catalog);
    case 'purchases': return p.purchases;
    case 'buyers': return p.buyers;
    case 'price': return p.price;
    case 'seasonal': return p.spentSeasonal;
    case 'classic': return p.spentClassic;
    case 'refunds': return p.refunds;
    case 'keys': return p.keys;
    default: return p.net;
  }
}

/** En-tête de colonne triable : un clic trie (puis inverse le sens), l'état est dit aux lecteurs d'écran (aria-sort). */
function SortTh<K extends string>({ id, label, sort, onSort, num, textual, title }: {
  id: K;
  label: string;
  sort: SortState<K>;
  onSort: (next: SortState<K>) => void;
  num?: boolean;
  textual?: boolean;
  title?: string;
}) {
  const active = sort.key === id;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={num ? lb.num : undefined} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'} title={title}>
      <button type="button" className={`${sh.sort} ${num ? sh.sortNum : ''} ${active ? sh.sortActive : ''}`}
        onClick={() => onSort(nextSort(sort, id, !!textual))}>
        {label}<Icon size={12} aria-hidden />
      </button>
    </th>
  );
}
